// frontend/src/components/grid/projectedAvailableColumn.js
//
// The Inventory grid's Projected available column.
//
// Projected available = Available + In transit, where Available is Shopify's:
// on hand − committed − unavailable. `unavailable` carries the reserved /
// damaged / safety_stock / quality_control states the Shopify admin shows as
// one "Unavailable" figure. It has no column here; it is on the wire for this
// getter.
//
// Every term is required, and required to be a NUMBER. The API Coalesces all
// four to 0 (api/views/inventory.py), so a missing or null one is a broken
// contract: render nothing rather than a figure the merchant would plan
// against. `Number(null)` is 0, so the guard checks the type rather than
// converting.
//
// The availability half is NOT spelled here: it is utils/availability,
// mirrored on the backend by core.utils.available_expr. What this module owns
// is the in-transit term and the column definition.

import { availableFrom } from '../../utils/availability';

/**
 * Projected available for a grid row, or null when it is not computable.
 *
 * Works on a data row, on a pinned totals row, and on a Shopify group header
 * alike: all three carry the same four wire names (the header sums them in
 * InventoryViewSet._group_headers, the pinned row in _compute_figures), so the
 * same getter forms the figure for all three. It returns null only when a term
 * is genuinely absent, which is a broken contract rather than a row shape.
 *
 * @param {object|undefined} row
 * @returns {number|null}
 */
export function projectedAvailableFrom(row) {
  if (!row) return null;
  const available = availableFrom(
    row.inventory_on_hand,
    row.inventory_committed,
    row.inventory_unavailable,
  );
  const inTransit = row.inventory_in_transit;
  if (available === null
      || typeof inTransit !== 'number' || !Number.isFinite(inTransit)) {
    return null;
  }
  return available + inTransit;
}

export function projectedAvailableColDef({ headerName }) {
  return {
    field: 'projected_available',
    headerName,
    type: 'numberColumn',
    valueGetter: (params) => projectedAvailableFrom(params.data),
  };
}
